import { BarChart } from "@mui/x-charts/BarChart";
import { Box, CircularProgress, Typography, Grid, useTheme, Paper } from "@mui/material";
import { ChangeCircle, TrendingUp, Assessment, ShowChart } from "@mui/icons-material";

const formatCurrency = (value: number | null | undefined) =>
    value == null ? '—' : new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(value);

import { KpiCard } from "../../programmeDashboard/components/KpiCard";
import { MetaDrivenTable } from "../../../shared/components";
import { useScopeChange } from "../hooks/useScopeChange";
import { useScopeChangeState } from "../hooks/useScopeChangeState";

export const ForecastVariance = () => {

    const theme = useTheme();

    const headers = [
        { "alias_name": "programme_name", "label": "Programme", "is_show": true, display_order: 1 },
        { "alias_name": "changes", "label": "Changes", "is_show": true, display_order: 1 },
        { "alias_name": "net_a_delta_percent", "label": "Net A Δ", "is_show": true, display_order: 1 },
        { "alias_name": "net_e_delta_percent", "label": "Net E Δ", "is_show": true, display_order: 1 },
        { "alias_name": "causes", "label": "Causes", "is_show": true, display_order: 1 },
        { "alias_name": "forecast_value", "label": "Forecast £", "is_show": true, display_order: 1 },
        { "alias_name": "variance_value", "label": "Variance £", "is_show": true, display_order: 1 },
    ];

    const { data: scopeChangeData, isLoading: isStateLoading, error: stateError, isError: isStateError } = useScopeChangeState();
    const { filteredData: tableData, chartData, isLoading: isScopeChangeLoading, error: scopeChangeError, isError: isScopeChangeError } = useScopeChange();

    if (isScopeChangeLoading || isStateLoading) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    if (isScopeChangeError || isStateError) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <Typography variant="h6" color="error">
                    {scopeChangeError?.message || stateError?.message || "Error loading data."}
                </Typography>
            </Box>
        )
    };

    return (
        <Box>

            <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2">
                    Scope Change & Forecast Variance
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Approved changes and forecasted performance
                </Typography>
            </Box>

            <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <KpiCard
                        label="Total Changes"
                        value={scopeChangeData.totalChanges}
                        icon={<ChangeCircle fontSize="large" />}
                        iconColor={theme.palette.info.main}
                        subValue="Approved changes"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <KpiCard
                        label="Net Scope Impact"
                        value={scopeChangeData.netScopeImpact}
                        icon={<TrendingUp fontSize="large" />}
                        iconColor={theme.palette.success.main}
                        subValue="Value change"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <KpiCard
                        label="Total Forecast Variance"
                        value={scopeChangeData.totalForecastVariance}
                        icon={<Assessment fontSize="large" />}
                        iconColor={theme.palette.warning.main}
                        subValue="vs. committed"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <KpiCard
                        label="Avg Variance %"
                        value={scopeChangeData.avgVariance}
                        icon={<ShowChart fontSize="large" />}
                        iconColor={theme.palette.error.main}
                        subValue="Across programmes"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
                <Grid size={{ xs: 12, md: 12 }} sx={{ height: { xs: "auto", md: 400 } }}>
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            minHeight: { xs: 300, md: "100%" },
                        }}
                    >
                        <BarChart
                            xAxis={[
                                {
                                    scaleType: "band",
                                    data: chartData.map((item: { programme: any; }) => item.programme),
                                    tickLabelStyle: { fontSize: 12 },
                                    height: 30,
                                },
                            ]}
                            yAxis={[
                                {
                                    tickLabelStyle: { fontSize: 12 },
                                },
                            ]}
                            series={[
                                {
                                    data: chartData.map((item: { approvedChanges: any; }) => item.approvedChanges),
                                    label: "Approved Changes",
                                    color: theme.palette.info.main,
                                },
                            ]}
                            grid={{ vertical: true, horizontal: true }}
                            borderRadius={4}
                        />
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <Paper variant="outlined" sx={{ m: 2, py: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2 }}>Forecast Variance Detail</Typography>
                        <MetaDrivenTable
                            headers={headers}
                            data={tableData}
                            dataMetrics={{
                                forecast_value: {
                                    type: 'component',
                                    element: (row: { forecast_value: number | null }) =>
                                        <>{formatCurrency(row.forecast_value)}</>
                                },
                                variance_value: {
                                    type: 'component',
                                    element: (row: { variance_value: number | null }) =>
                                        <>{formatCurrency(row.variance_value)}</>
                                },
                            }}
                            sortDisabled
                        />
                    </Paper>
                </Grid>
            </Grid>

        </Box>
    );
};